import i18next from "@/lib/i18n";
import { translateServerMessage } from "@/lib/apiErrors";
import { buildStaleBuildParams } from "@/lib/buildChanges";
import {
  translateBatchFailure,
  translateSystemNotification,
} from "@/features/notifications/lib/translateSystemNotification";

/**
 * Minimal shape a notification must expose to be localized. Satisfied by both
 * the REST `Notification` row and the WS `notification.new` message.
 *
 * `extra_data` is optional: the backend does not currently include it on either
 * surface, and a follow-up backend change will start emitting structured params
 * (e.g. `route_id` / `route_name`, or an `i18n_key`). Until then the mapping
 * falls back to the raw backend `title` / `body`, so it degrades gracefully both
 * before and after that lands.
 */
export interface TranslatableNotification {
  notification_type?: string | null;
  title?: string | null;
  body?: string | null;
  extra_data?: Record<string, unknown> | null;
}

/** Localized `title` / `body` pair rendered by the bell and the toast. */
export interface LocalizedNotification {
  title: string;
  body: string | undefined;
}

/**
 * i18n mapping keyed on `notification_type`. Each entry names the title/body
 * i18n keys under `notifications.types.*`. `bodyParams` lists the `extra_data`
 * keys the body interpolation depends on; if any is missing from the payload the
 * body falls back to the raw backend string (rather than rendering a template
 * with a blank slot).
 */
interface TypeMapping {
  titleKey?: string;
  bodyKey?: string;
  /** `extra_data` keys required to interpolate `bodyKey`, in read order. */
  bodyParams?: string[];
}

const TYPE_MAP: Record<string, TypeMapping> = {
  route_created: {
    titleKey: "notifications.types.route_created.title",
    bodyKey: "notifications.types.route_created.body",
    bodyParams: ["route_name", "route_id"],
  },
  route_changed: {
    titleKey: "notifications.types.route_changed.title",
    bodyKey: "notifications.types.route_changed.body",
    bodyParams: ["route_name", "route_id"],
  },
  chat_message: {
    titleKey: "notifications.types.chat_message.title",
    // Chat body is the free-text message the sender wrote — keep it verbatim.
  },
  broadcast: {
    titleKey: "notifications.types.broadcast.title",
    // Broadcast body is admin-authored free text — keep it verbatim.
  },
  routes_built: {
    // BUG-350 kept this type VERBATIM because the counts lived only in the
    // backend's prose. Those strings were hardcoded RUSSIAN, so an EN dispatcher
    // read Russian (TASK-4743). The backend now also emits a machine-readable
    // outcome — `extra_data.code` (ROUTES_BUILD_SUCCESS / _PARTIAL / _NONE) plus
    // `extra_data.params` with the counts — which `translateBuildCode` below
    // renders in the VIEWER's language. The raw title/body remain the fallback
    // for rows written before that backend change, preserving BUG-322's
    // graceful degradation.
  },
};

/**
 * Failure `notification_type`s whose backend title/body are deliberately English
 * (`BUILD_FAILURE_TITLE` in `routes/tasks.py`, `OPTIMIZATION_FAILURE_TITLE` in
 * `planning/tasks.py`, `GENERIC_FAILURE_TEXT` in `core/text.py`).
 *
 * Sprint 174 review: TASK-4740/4743 replaced the previously RUSSIAN server prose
 * with a stable `extra_data.code` + an English fallback, on the understanding
 * that the client would localize it — and it did not, so a Russian dispatcher
 * read English on the highest-severity notification the platform sends. These
 * two types are the localization entry point: the TITLE always comes from the
 * bundle, and the BODY comes from `failureCodes.<CODE>` when the backend named a
 * code this bundle knows, else from the type's generic sentence.
 */
const FAILURE_TYPE_KEYS: Record<string, { titleKey: string; bodyKey: string }> =
  {
    routes_build_failed: {
      titleKey: "notifications.failureTitles.routes_build_failed",
      bodyKey: "notifications.failureBodies.routes_build_failed",
    },
    optimization_failed: {
      titleKey: "notifications.failureTitles.optimization_failed",
      bodyKey: "notifications.failureBodies.optimization_failed",
    },
  };

/**
 * Failure codes carried in `extra_data.code`, each with a
 * `notifications.failureCodes.<CODE>.{title,body}` entry in BOTH bundles.
 *
 * Enumerated for the same reason as `BUILD_CODE_KEYS`: `t()` on an unvalidated
 * remote string renders the raw key path when the bundle does not know it. A
 * failure notification is exactly where a raw `notifications.failureCodes.X.body`
 * would be least forgivable, so an unknown code falls back to the type's generic
 * localized sentence rather than to a key path.
 *
 * Emitted by (verified against the backend, not guessed):
 *   - `routes/tasks.py`  ROUTES_BUILD_FAILED + whatever code the raised exception
 *     carries, resolved through the SAME `resolve_code` precedence chain the HTTP
 *     error envelope uses — so any registry code can arrive here, not just the
 *     generic fallback. `VALIDATION_ERROR` remains the fallback (+ `reason`), and
 *     `ROUTE_ORDER_UNPLACEABLE` is emitted by the up-front capacity guard.
 *   - `routes/tasks.py`  ROUTES_BUILD_FAILED + INTERNAL_ERROR (no `reason`)
 *   - `planning/tasks.py:477` OPTIMIZATION_FAILED + PLANNING_MATRIX_UNAVAILABLE
 *     (+ `reason`)
 *   - `routes/tasks.py`  ROUTES_BUILD_FAILED + ROUTE_BUILD_STALE (BUG-672): the
 *     lock-free auto-build found its orders or vans changed between planning and
 *     the commit transaction, so it wrote nothing. The body tells the dispatcher to
 *     re-run the build; any `reason` is ignored by the copy.
 * The batch summary at `planning/tasks.py:141` is also OPTIMIZATION_FAILED but
 * carries counts and NO `code`, so it lands on the generic body by design.
 */
const FAILURE_CODE_KEYS = new Set([
  "VALIDATION_ERROR",
  "INTERNAL_ERROR",
  "PLANNING_MATRIX_UNAVAILABLE",
  "ROUTE_ORDER_UNPLACEABLE",
  "ROUTE_BUILD_STALE",
]);

/**
 * Codes whose body is composed from `params` (a `_figures` context variant), keyed by
 * code. BUG-687…693: `ROUTE_BUILD_STALE` names how many orders changed and what —
 * «Маршруты не сохранены: пока шло построение, изменили 2 заказа (окно доставки)…».
 * An absent/empty `params` returns `{}` → the figure-less base sentence.
 */
const FAILURE_PARAM_BUILDERS: Record<
  string,
  (params: Record<string, unknown> | null) => Record<string, unknown>
> = {
  ROUTE_BUILD_STALE: (params) => buildStaleBuildParams(params, i18next.t),
};

/**
 * Codes whose localized body names the affected ORDERS via `{{orders}}` instead of
 * interpolating the backend's English `{{reason}}`.
 *
 * Sprint 174 review: `ROUTE_ORDER_UNPLACEABLE` previously collapsed into
 * `VALIDATION_ERROR`, so a Russian dispatcher read a Russian frame wrapped around
 * an ENGLISH server sentence naming orders by raw numeric id
 * («Запрос отклонён: Orders [69784, 69785] cannot fit any available vehicle.»).
 * The backend now sends the code plus an `unassigned` array carrying each order's
 * title/address, so the whole sentence is the viewer's language and the orders are
 * named the way the dispatcher knows them.
 */
const ORDER_NAMING_CODES = new Set(["ROUTE_ORDER_UNPLACEABLE"]);

/** How many order names are listed inline before collapsing into "+N more". */
const MAX_NAMED_ORDERS = 3;

interface UnassignedEntry {
  order_id?: unknown;
  order_title?: unknown;
  order_address?: unknown;
}

/**
 * Renders the affected orders as a human list — title first, then address, and only
 * as a last resort the id, so a row whose order was deleted still says something.
 * Returns `null` when the payload carries no usable entries, which keeps the caller
 * on the `{{reason}}` sentence rather than rendering an empty list.
 */
function formatAffectedOrders(
  extraData: Record<string, unknown> | null | undefined,
): string | null {
  const raw = extraData?.unassigned;
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const names = raw
    .map((entry) => {
      const item = entry as UnassignedEntry;
      const title =
        typeof item.order_title === "string" && item.order_title.trim()
          ? item.order_title.trim()
          : null;
      if (title) return title;
      const address =
        typeof item.order_address === "string" && item.order_address.trim()
          ? item.order_address.trim()
          : null;
      if (address) return address;
      return item.order_id !== undefined && item.order_id !== null
        ? `#${String(item.order_id)}`
        : null;
    })
    .filter((name): name is string => name !== null);

  if (names.length === 0) return null;

  const total =
    typeof extraData?.unassigned_total === "number" &&
    extraData.unassigned_total > names.length
      ? extraData.unassigned_total
      : names.length;

  const shown = names.slice(0, MAX_NAMED_ORDERS);
  const hidden = total - shown.length;
  const list = shown.join(", ");
  return hidden > 0
    ? i18next.t("notifications.affectedOrdersMore", { list, count: hidden })
    : list;
}

/**
 * Codes emitted in `extra_data.code` by the async route build, each with a
 * `notifications.buildCodes.<CODE>.{title,body}` locale entry in both bundles.
 *
 * Listed explicitly rather than interpolating whatever string the backend sent:
 * a `t()` call on an unvalidated remote value would render a raw key path (or a
 * blank line) for a code this bundle does not know, which is the same class of
 * defect as the server's old `_REASON_RU.get(dominant, "")`.
 */
const BUILD_CODE_KEYS = new Set([
  "ROUTES_BUILD_SUCCESS",
  "ROUTES_BUILD_PARTIAL",
  "ROUTES_BUILD_NONE",
]);

/**
 * Renders a build-outcome code in the viewer's language, or `null` when the
 * payload carries no code this bundle recognizes (→ keep the raw backend text).
 *
 * NOTE FOR REVIEWERS: `notifications.buildCodes.${code}` is a DYNAMICALLY built
 * key, and `npm run check:i18n` cannot see dynamic keys — deleting one of these
 * entries keeps that gate green. The rendered-text tests in
 * `translateNotification.test.ts` are the only thing that catches it.
 */
function translateBuildCode(
  extraData: Record<string, unknown> | null | undefined,
): LocalizedNotification | null {
  const code = extraData?.code;
  if (typeof code !== "string" || !BUILD_CODE_KEYS.has(code)) return null;

  const rawParams = extraData?.params;
  const params =
    typeof rawParams === "object" &&
    rawParams !== null &&
    !Array.isArray(rawParams)
      ? (rawParams as Record<string, unknown>)
      : {};

  return {
    title: i18next.t(`notifications.buildCodes.${code}.title`),
    body: i18next.t(`notifications.buildCodes.${code}.body`, params),
  };
}

/**
 * Localizes a failure notification. Returns `null` for any type that is not a
 * failure type, so the caller continues down the normal `TYPE_MAP` path.
 *
 * NOTE FOR REVIEWERS: `notifications.failureCodes.${code}` is a DYNAMICALLY built
 * key and `npm run check:i18n` only scans calls whose argument is a string
 * literal, so deleting one of those entries keeps that gate green. The rendered-text
 * assertions in `translateNotification.test.ts` and
 * `useNotificationsWebSocket.toastRouting.test.tsx` are the only protection.
 */
function translateFailure(
  type: string | undefined,
  extraData: Record<string, unknown> | null | undefined,
): LocalizedNotification | null {
  const typeKeys = type ? FAILURE_TYPE_KEYS[type] : undefined;
  if (!typeKeys) return null;

  // BUG-677: the batch summary (`optimization_failed` with counts, no code).
  if (type === "optimization_failed") {
    const batch = translateBatchFailure(extraData);
    if (batch) return batch;
  }

  // The title is a fixed platform sentence per type — never the server's English.
  const title = i18next.t(typeKeys.titleKey);

  const code = extraData?.code;
  // `reason` is the backend's sanitized detail (`sanitize_error_text`) — English.
  const rawReason = extraData?.reason;
  const reason = typeof rawReason === "string" ? rawReason : "";
  const rawParams = extraData?.params;
  const params =
    typeof rawParams === "object" &&
    rawParams !== null &&
    !Array.isArray(rawParams)
      ? (rawParams as Record<string, unknown>)
      : null;

  if (typeof code === "string" && FAILURE_CODE_KEYS.has(code)) {
    // The reason is the only fragment that may stay in the server's language;
    // the sentence around it is the viewer's. Absent (INTERNAL_ERROR) it
    // interpolates to nothing, so those entries deliberately carry no
    // {{reason}} placeholder.

    // Codes that name the affected orders render a fully localized sentence and never
    // interpolate the server's English `reason`. If the payload carries no usable order
    // names (an older row written before the backend change), fall back to the generic
    // localized body for the type — NOT to the English reason, which is the whole defect.
    if (ORDER_NAMING_CODES.has(code)) {
      const orders = formatAffectedOrders(extraData);
      return {
        title: i18next.t(`notifications.failureCodes.${code}.title`),
        body: orders
          ? i18next.t(`notifications.failureCodes.${code}.body`, { orders })
          : i18next.t(typeKeys.bodyKey),
      };
    }

    // BUG-677: VALIDATION_ERROR is the umbrella; when the reason is a sentence
    // the shared prose maps know, the translated sentence IS the cause and the
    // «Запрос отклонён: <English>» frame is not needed.
    if (code === "VALIDATION_ERROR" && reason) {
      const resolved = translateServerMessage(reason, i18next.t, null, params);
      if (resolved.translated) return { title, body: resolved.message };
    }

    return {
      title: i18next.t(`notifications.failureCodes.${code}.title`),
      body: i18next.t(`notifications.failureCodes.${code}.body`, {
        reason,
        ...(FAILURE_PARAM_BUILDERS[code]?.(params) ?? {}),
      }),
    };
  }

  // BUG-677: any other code (the build path resolves the raise site's own code,
  // so every registry code can arrive — ROUTE_QUOTA_EXHAUSTED, QUOTA_EXCEEDED,
  // ORDER_NOT_GEOCODED, PLANNING_WAREHOUSE_NOT_ELIGIBLE, …) goes through the SAME
  // code/prose maps the HTTP errors use, with its figures. Before, it collapsed to
  // the generic «завершилось ошибкой» and the real cause was lost.
  if (reason || typeof code === "string") {
    const resolved = translateServerMessage(
      reason,
      i18next.t,
      typeof code === "string" ? code : null,
      params,
    );
    if (resolved.translated) return { title, body: resolved.message };
  }

  // Last resort — a cause this bundle has no words for. The server's sentence is
  // kept (it is the only statement of the cause) but framed in the viewer's
  // language, so a Russian dispatcher reads «… Сообщение сервера: «…»» rather
  // than a bare English sentence that looks like a broken screen.
  if (reason) {
    return {
      title,
      body: i18next.t("notifications.failureWithServerMessage", {
        failure: i18next.t(typeKeys.bodyKey),
        message: reason,
      }),
    };
  }

  // No code and no reason (an older row): still localized, without the cause.
  return { title, body: i18next.t(typeKeys.bodyKey) };
}

/**
 * BUG-679: the localized title/body of a failed route build, from the failure's
 * `code` / `params` / `reason` — the SAME rendering the ROUTES_BUILD_FAILED
 * notification gets, so the build poller (which reads the task payload, not a
 * notification) cannot word one failure differently from the bell.
 */
export function translateBuildFailure(
  extraData: Record<string, unknown> | null | undefined,
): LocalizedNotification {
  // `routes_build_failed` is in FAILURE_TYPE_KEYS, so the fallback is unreachable.
  return (
    translateFailure("routes_build_failed", extraData) ?? {
      title: i18next.t("notifications.failureTitles.routes_build_failed"),
      body: i18next.t("notifications.failureBodies.routes_build_failed"),
    }
  );
}

/**
 * The type's generic sentences — what a failure renders when nothing more
 * specific is known (no code, INTERNAL_ERROR). Used to rank two renderings of
 * one failure (BUG-679).
 */
export function genericBuildFailureBodies(): string[] {
  return [
    i18next.t("notifications.failureBodies.routes_build_failed"),
    i18next.t("notifications.failureCodes.INTERNAL_ERROR.body"),
  ];
}

/**
 * Localizes `driver_account_deletion_requested` (TASK-4775).
 *
 * Kept out of `TYPE_MAP` on purpose: that path interpolates a SINGLE param
 * (`resolveBodyParam` returns the first present key as `{{route}}`), and this
 * body needs TWO — the driver's name and the erasure date. Bending the shared
 * helper to carry two params would change behaviour for every existing type.
 *
 * The backend deliberately sends no prose (DEV_RULES §6.4): the whole sentence
 * is built here from `extra_data`. `deletion_scheduled_for` is an ISO-8601
 * string, rendered in the viewer's locale. When either fact is missing the body
 * falls back to the raw backend string, matching BUG-322's graceful degradation.
 *
 * NOTE FOR REVIEWERS: these keys ARE static literals, so `check:i18n` does cover
 * them — unlike the dynamic `failureCodes.${code}` family above. The rendered-text
 * test in `translateNotification.test.ts` pins the RU/EN output regardless.
 */
function translateDriverAccountDeletion(
  type: string | undefined,
  extraData: Record<string, unknown> | null | undefined,
  rawBody: string | undefined,
): LocalizedNotification | null {
  if (type !== "driver_account_deletion_requested") return null;

  const title = i18next.t(
    "notifications.types.driver_account_deletion_requested.title",
  );

  const rawDriver = extraData?.driver_name;
  const driver = typeof rawDriver === "string" ? rawDriver.trim() : "";
  const rawDate = extraData?.deletion_scheduled_for;
  const parsed = typeof rawDate === "string" ? new Date(rawDate) : null;
  const hasDate = parsed !== null && !Number.isNaN(parsed.getTime());

  if (!driver || !hasDate) {
    return { title, body: rawBody };
  }

  return {
    title,
    body: i18next.t(
      "notifications.types.driver_account_deletion_requested.body",
      {
        driver,
        date: parsed.toLocaleDateString(i18next.language),
      },
    ),
  };
}

/**
 * Reads the first present `bodyParams` key from `extra_data`, coercing to a
 * display string. Returns `undefined` when none of the candidate keys are
 * present (signals a fall back to the raw body).
 */
function resolveBodyParam(
  extraData: Record<string, unknown> | null | undefined,
  keys: string[],
): string | undefined {
  if (!extraData) return undefined;
  for (const key of keys) {
    const value = extraData[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return undefined;
}

/**
 * Localizes a notification's `title`/`body` in the viewer's current language,
 * keyed on `notification_type`.
 *
 * - Failure types (`routes_build_failed` / `optimization_failed`) are localized
 *   from `extra_data.code`, never from the backend's English literals.
 * - Unknown types fall back to the raw backend `title` / `body`.
 * - A translatable title always uses the localized string (route titles need no
 *   params).
 * - A body that needs interpolation params falls back to the raw body whenever a
 *   required `extra_data` value is absent, so no broken template is shown.
 */
export function translateNotification(
  notification: TranslatableNotification,
): LocalizedNotification {
  const rawTitle = notification.title ?? "";
  const rawBody = notification.body ? notification.body : undefined;

  const type = notification.notification_type ?? undefined;
  const mapping = type ? TYPE_MAP[type] : undefined;

  // A failure type is fully localized (title always, body from the code when the
  // backend named one) — its server-side title/body are ENGLISH literals, so a
  // fall-through here is what made a Russian dispatcher read English.
  const fromFailure = translateFailure(type, notification.extra_data);
  if (fromFailure) return fromFailure;

  // BUG-677: billing, invoice, planning `route_changed` variants and the chat
  // photo placeholder — their backend title/body are English platform copy.
  const fromSystem = translateSystemNotification(notification);
  if (fromSystem) return fromSystem;

  // A staffing notification whose entire copy is client-side (the backend sends
  // structured facts only), so it must not fall through to the raw English.
  const fromDeletion = translateDriverAccountDeletion(
    type,
    notification.extra_data,
    rawBody,
  );
  if (fromDeletion) return fromDeletion;

  // An outcome code wins over the raw (Russian) title/body whenever the backend
  // supplied one, so the dispatcher reads their OWN language.
  const fromCode = translateBuildCode(notification.extra_data);
  if (fromCode) return fromCode;

  if (!mapping) {
    return { title: rawTitle, body: rawBody };
  }

  let title = rawTitle;
  if (mapping.titleKey) {
    title = i18next.t(mapping.titleKey);
  }

  let body = rawBody;
  if (mapping.bodyKey) {
    const param = mapping.bodyParams
      ? resolveBodyParam(notification.extra_data, mapping.bodyParams)
      : undefined;
    // Only render the interpolated body when the required param is available;
    // otherwise keep the raw backend body (BUG-322 graceful degradation).
    if (!mapping.bodyParams || param !== undefined) {
      body = i18next.t(mapping.bodyKey, { route: param });
    }
  }

  return { title, body };
}
